import axios from 'crypto-fetch/axios.js'
import { cryptoFetch } from 'crypto-fetch'

/**
 * Fetches betting vouchers based on specified criteria.
 *
 * @param {Object} options - The options for fetching vouchers.
 * @param {Number} options.bizType - The business type of the voucher:
 *                                   1 - Real sports
 *                                   2 - Virtual sports
 *                                   3 - Jackpot
 * @param {Array<number>} [options.status=[]] - The status of the vouchers to fetch:
 *                                  1 - Available
 *                                  2 - Used
 *                                  3 - Expired
 *                                  4 - Unavailable
 * @returns {Promise} A promise that resolves with the voucher data from the API.
 */
export const fetchBetVouchers = ({ bizType = 1, status = [] }) => {
  const body = { bizType, status }
  return axios.post('/marketing/market/voucher/list', body)
}

/**
 * Fetches a list of vouchers without pagination (new API version).
 *
 * @param {Object} options - The options for fetching vouchers.
 * @param {Array<number>} [options.status=[]] - The status of the vouchers to fetch:
 *                                              1 - Available
 *                                              2 - Used
 *                                              3 - Expired
 *                                              4 - Unavailable
 * @param {number} [options.tag=0] - The tag to filter vouchers:
 *                                   0 - All (default if empty)
 *                                   1 - Sports
 *                                   2 - Super Kick
 *                                   3 - Virtual
 *                                   4 - Min Stake
 *                                   5 - Expire Soon
 *                                   6 - No Min Stake
 *                                   7 - Upcoming
 *                                   8 - App Only
 *                                   9 - Mad Punch Only
 *                                   10 - Galaxy Gatherers Only
 *                                   11 - Gem Hunter Only
 * @returns {Promise} A promise that resolves with the voucher list data from the API.
 */
export const fetchVoucherListV2 = ({ status = [], tag = 0 }) => {
  const body = { status, tag: Number(tag) }
  return axios.post('/marketing/market/voucher/list/v2', body)
}

/**
 * Fetches a paginated list of vouchers.
 *
 * @param {Object} options - The options for fetching vouchers.
 * @param {number} [options.pageNum=1] - The page number to retrieve.
 * @param {Array<number>} [options.status=[]] - The status of the vouchers to fetch:
 *                                              1 - Available
 *                                              2 - Used
 *                                              3 - Expired
 *                                              4 - Unavailable
 * @returns {Promise<AxiosResponse>} The Axios response with voucher data.
 */
export const fetchPaginatedVouchers = ({ pageNum = 1, status = [] }) => {
  const body = { pageNum, status }
  return axios.post('/marketing/market/voucher/page', body)
}

/**
 * Redeems a voucher code.
 * @param {string} code - The voucher code to be redeemed. Defaults to an empty string if not provided.
 * @returns {Promise<AxiosResponse>} A promise that resolves with the Axios response object.
 */
export const redeemVoucherCode = (code = '') => {
  if (!code) {
    throw new Error('Voucher code is required')
  }
  return axios.get(`/marketing/market/voucher/${code}/redeem`)
}

/**
 * Fetches the total amount of vouchers issued since the site's launch.
 * @returns {Promise<number>} A promise that resolves with the total amount of vouchers issued.
 *
 */
export const fetchTotalVoucherAmount = async () => {
  const data = await axios.get('/marketing/market/voucher/summarize/all')
  return data.amount
}

/**
 * Fetches the distribution count for a specific voucher plan.
 * @param {string} id - The unique identifier of the voucher plan.
 * @returns {Promise<AxiosResponse>} A promise that resolves with the Axios response object.
 */
export const fetchVoucherPlanDistributionCount = (id = '') => {
  if (!id) {
    throw new Error('Voucher plan ID is required')
  }
  return axios.get(`/marketing/market/voucher/plan/${id}/send-count`)
}
/**
 * Claims a voucher for a specific event and plan.
 * @param {string} eventId - The unique identifier of the event.
 * @param {string} planId - The identifier of the voucher plan.
 * @returns {Promise<any>} A promise that resolves with the response from the cryptoFetch call.
 */
export const claimEventVoucher = (eventId = '', planId = '') => {
  const body = {
    traceId: eventId,
    planId
  }
  return cryptoFetch('/marketing/market/voucher/quick/get', body, 2)
}

/**
 * Fetches voucher distribution records based on activity category.
 * @param {Object} options - The options for filtering the voucher records.
 * @param {number} [options.kind=1] - The category or type of activity. Defaults to 1. 1（首冲活动）7(彩蛋)
 * @param {string} [options.activityId] - Optional. The unique identifier of a specific activity.
 * @param {number} [options.offsetHours] - Optional. Time offset in hours to filter the records.
 * @returns {Promise<AxiosResponse>} A promise that resolves with the Axios response object
 */
export const fetchVoucherDistributionRecords = ({ kind = 1, activityId, offsetHours }) => {
  const params = { kind }
  if (activityId) params.activityId = activityId
  if (offsetHours) params.offsetHours = offsetHours
  return axios.get('/marketing/market/voucher/delivery/record', { params })
}

/**
 * Fetches statistics for vouchers expiring on a specific date.
 * @param {string} date - The date for which to fetch expiration statistics,
 *                        expected in a format compatible with the API (e.g., 'YYYYMMDD').
 * @returns {Promise<AxiosResponse>} A promise that resolves with the Axios response object.
 * @throws {AxiosError} Throws an error if the API request fails.
 */
export const fetchDailyVoucherExpirationStats = (date) => {
  const params = { date }
  return axios.get('/marketing/market/voucher/expired/statistic', { params })
}
